import type { ReactNode } from 'react';

type PaginationProps = {
  /** 1-based current page. */
  page: number;
  pageSize: number;
  total: number;
  pageSizeOptions?: number[];
  /** Disables every control (e.g. while a fetch is in flight). */
  disabled?: boolean;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  /** Noun for the result summary ("Showing 1–50 of 120 users"). */
  itemLabel?: string;
};

const DOTS = 'dots';

function range(start: number, end: number): number[] {
  const out: number[] = [];
  for (let i = start; i <= end; i++) out.push(i);
  return out;
}

/**
 * Classic truncated page range with a fixed window around the current page,
 * always anchoring page 1 and the last page:
 *   1 … 6 7 8 … 40
 * `siblings` = neighbours shown on each side of the current page.
 */
function pageRange(current: number, totalPages: number, siblings = 1): (number | typeof DOTS)[] {
  // first + last + current + 2·siblings + 2 dots
  const slots = siblings * 2 + 5;
  if (slots >= totalPages) return range(1, totalPages);

  const left = Math.max(current - siblings, 1);
  const right = Math.min(current + siblings, totalPages);
  const showLeftDots = left > 2;
  const showRightDots = right < totalPages - 1;

  if (!showLeftDots && showRightDots) {
    return [...range(1, 3 + 2 * siblings), DOTS, totalPages];
  }
  if (showLeftDots && !showRightDots) {
    return [1, DOTS, ...range(totalPages - (3 + 2 * siblings) + 1, totalPages)];
  }
  return [1, DOTS, ...range(left, right), DOTS, totalPages];
}

/**
 * Reusable admin-table pagination: page-size selector + result summary +
 * truncated numbered page nav with Prev/Next. Accessible (nav landmark,
 * aria-current on the active page, labelled controls). Page is 1-based.
 */
export function Pagination({
  page,
  pageSize,
  total,
  pageSizeOptions = [25, 50, 100, 200],
  disabled = false,
  onPageChange,
  onPageSizeChange,
  itemLabel = 'results',
}: PaginationProps) {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const safePage = Math.min(Math.max(page, 1), totalPages);
  const from = total === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const to = Math.min(safePage * pageSize, total);
  const items = pageRange(safePage, totalPages);

  const go = (p: number): void => {
    const clamped = Math.min(Math.max(p, 1), totalPages);
    if (clamped !== safePage) onPageChange(clamped);
  };

  return (
    <div className="flex flex-col gap-3 text-sm text-white/60 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
        <label className="flex items-center gap-2">
          <span className="text-white/55">Per page</span>
          <select
            value={pageSize}
            disabled={disabled}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="rounded-md border border-white/15 bg-black/30 px-2 py-1 text-white focus:border-white/40 focus:outline-none disabled:opacity-50"
          >
            {pageSizeOptions.map((o) => (
              <option key={o} value={o}>
                {o}
              </option>
            ))}
          </select>
        </label>
        <span>
          {total === 0 ? `No ${itemLabel}` : `Showing ${from}–${to} of ${total} ${itemLabel}`}
        </span>
      </div>

      {totalPages > 1 && (
        <nav aria-label="Pagination" className="flex items-center gap-1">
          <PageButton
            label="Previous page"
            disabled={disabled || safePage <= 1}
            onClick={() => go(safePage - 1)}
          >
            &lsaquo;
          </PageButton>

          {items.map((it, idx) =>
            it === DOTS ? (
              <span key={`dots-${idx}`} aria-hidden="true" className="px-1.5 text-white/35">
                &hellip;
              </span>
            ) : (
              <PageButton
                key={it}
                label={`Page ${it}`}
                active={it === safePage}
                disabled={disabled}
                onClick={() => go(it)}
              >
                {it}
              </PageButton>
            ),
          )}

          <PageButton
            label="Next page"
            disabled={disabled || safePage >= totalPages}
            onClick={() => go(safePage + 1)}
          >
            &rsaquo;
          </PageButton>
        </nav>
      )}
    </div>
  );
}

function PageButton({
  children,
  label,
  onClick,
  disabled,
  active,
}: {
  children: ReactNode;
  label: string;
  onClick: () => void;
  disabled?: boolean;
  active?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={label}
      aria-current={active ? 'page' : undefined}
      className={[
        'min-w-[34px] rounded-md border px-2 py-1 text-center tabular-nums transition',
        'focus:outline-none focus-visible:ring-2 focus-visible:ring-white',
        'disabled:cursor-not-allowed disabled:opacity-40',
        active
          ? 'border-brand-redAccent bg-brand-redAccent/20 font-semibold text-white'
          : 'border-white/15 text-white/80 hover:bg-white/10',
      ].join(' ')}
    >
      {children}
    </button>
  );
}
